import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, Briefcase, AlertTriangle, Receipt, Headphones, CalendarDays, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { PortalHeader } from "./PortalHeader";
import { FeedbackModal } from "./FeedbackModal";
import { FloatingWidgets } from "./FloatingWidgets";
import { SettingsDialog } from "./SettingsDialog";
import { MfaNotice } from "./MfaNotice";

const layer1Items = [
  {
    id: "overview",
    label: "Overview",
    icon: LayoutDashboard,
    path: "/",
  },
  {
    id: "service-orders",
    label: "Service Orders",
    icon: Briefcase,
    path: "/service-orders",
  },
  {
    id: "meetings",
    label: "Meetings",
    icon: CalendarDays,
    path: "/meetings",
  },
  {
    id: "invoices",
    label: "Invoices",
    icon: Receipt,
    path: "/invoices",
  },
  {
    id: "support",
    label: "Support",
    icon: Headphones,
    path: "/support",
  },
  {
    id: "disputes",
    label: "Disputes",
    icon: AlertTriangle,
    path: "/disputes",
  },
];

function SidebarButton({
  item,
  isActive,
  onClick,
}: {
  item: { id: string; label: string; icon: React.ElementType; path: string };
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 text-left group",
        isActive
          ? "bg-primary/10 text-primary border-l-2 border-primary"
          : "text-muted-foreground hover:text-foreground hover:bg-white/5",
      )}
    >
      <item.icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{item.label}</span>
    </button>
  );
}

export function PortalLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <PortalHeader />

      <aside className="w-56 fixed top-[65px] left-0 bottom-0 border-r border-border/50 bg-card/30 z-30 overflow-y-auto flex flex-col">
        <div className="p-4 flex-1">
          <nav className="space-y-1">
            {layer1Items.map((item) => (
              <SidebarButton
                key={item.id}
                item={item}
                isActive={item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path)}
                onClick={() => navigate(item.path)}
              />
            ))}
          </nav>
        </div>

        <div className="p-4 border-t border-border/50">
          <button
            onClick={() => setSettingsOpen(true)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 text-left group",
              "text-muted-foreground hover:text-foreground hover:bg-white/5"
            )}
          >
            <Settings className="h-4 w-4 shrink-0" />
            <span className="flex-1">Settings</span>
          </button>
        </div>
      </aside>

      <main className="ml-56 p-8 overflow-auto min-h-[calc(100vh-65px)]">
        <MfaNotice />
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Outlet />
        </motion.div>
      </main>

      <FloatingWidgets />
      <FeedbackModal />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
